/**
 * @fileoverview Component Library - Dev Only
 *
 * Purpose: Showcase all UI components for development and testing
 * URL: /component-library
 * Protection: Dev-only (multi-layer security check)
 *
 * Structure:
 * - Main layout and navigation
 * - Sections imported from separate files for maintainability:
 *   - Colors section (-sections.colors.tsx)
 *   - Primitives section (-sections.primitives.tsx)
 *   - Composed section (-sections.composed.tsx)
 *   - Settings section (-sections.settings.tsx)
 *
 * Dependencies:
 * - All components from @/ui
 */

import { createFileRoute } from "@tanstack/react-router";
import { SectionNav } from "./-ui.section-nav";
import { ThemeSwitcher } from "@/ui/settings";
import { ColorsSection } from "./-sections.colors";
import { TypographySection } from "./-sections.typography";
import { DesignTokensSection } from "./-sections.design-tokens";
import { IconsSection } from "./-sections.icons";
import { PrimitivesSection } from "./-sections.primitives";
import { ComposedSection } from "./-sections.composed";
import { SettingsSection } from "./-sections.settings";
import { SonnerToaster, ToastToaster } from "@/ui/composed/notifications";
import { Separator } from "@/ui/primitives";

export const Route = createFileRoute("/_app/_dev/component-library")({
  component: ComponentLibrary,
  beforeLoad: () => ({
    title: "Component Library - Dev Only",
  }),
});

function ComponentLibrary() {
  return (
    <div className="min-h-screen bg-background">
      {/* Toast providers */}
      <SonnerToaster />
      <ToastToaster />
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center">
          <div className="flex flex-1 items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Component Library</h1>
              <p className="text-sm text-muted-foreground">
                Development Only - Design System Showcase
              </p>
            </div>

            {/* Right Side: Theme Switcher & Dev Mode Indicator */}
            <div className="flex items-center gap-3">
              {/* Theme Switcher */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Theme:</span>
                <ThemeSwitcher />
              </div>

              {/* Dev Mode Indicator */}
              <div className="flex items-center gap-2 rounded-md bg-yellow-500/10 px-3 py-1.5 text-yellow-600 dark:text-yellow-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                  <path d="M3.6 9h16.8" />
                  <path d="M3.6 15h16.8" />
                  <path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
                </svg>
                <span className="text-sm font-medium">DEV MODE</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-12">
        <div className="flex gap-8">
          {/* Sidebar Navigation - Hidden on mobile */}
          <aside className="hidden w-56 flex-shrink-0 lg:block">
            <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto">
              <SectionNav
                sections={[
                  { id: "introduction", label: "Introduction" },
                  {
                    id: "colors",
                    label: "Colors",
                    subsections: [
                      { id: "primary-colors", label: "Primary Colors" },
                      { id: "semantic-colors", label: "Semantic Colors" },
                      { id: "surface-colors", label: "Surface Colors" },
                      { id: "border-colors", label: "Border & Input" },
                    ],
                  },
                  { id: "typography", label: "Typography" },
                  { id: "design-tokens", label: "Design Tokens" },
                  { id: "icons", label: "Icons" },
                  {
                    id: "components",
                    label: "Components",
                    subsections: [
                      { id: "accordion", label: "Accordion" },
                      { id: "alert-dialogs", label: "Alert Dialogs" },
                      { id: "alerts", label: "Alerts" },
                      { id: "aspect-ratio", label: "Aspect Ratio" },
                      { id: "avatars", label: "Avatars" },
                      { id: "badges", label: "Badges" },
                      { id: "breadcrumb", label: "Breadcrumb" },
                      { id: "button-group", label: "Button Group" },
                      { id: "buttons", label: "Buttons" },
                      { id: "calendar", label: "Calendar" },
                      { id: "cards", label: "Cards" },
                      { id: "carousel", label: "Carousel" },
                      { id: "chart", label: "Chart" },
                      { id: "checkboxes", label: "Checkboxes" },
                      { id: "collapsible", label: "Collapsible" },
                      { id: "combobox", label: "Combobox" },
                      { id: "command", label: "Command" },
                      { id: "context-menus", label: "Context Menus" },
                      { id: "data-table", label: "Data Table" },
                      { id: "date-picker", label: "Date Picker" },
                      { id: "dialogs", label: "Dialogs" },
                      { id: "drawer", label: "Drawer" },
                      { id: "dropdown-menus", label: "Dropdown Menus" },
                      { id: "empty", label: "Empty" },
                      { id: "field", label: "Field" },
                      { id: "form", label: "Form" },
                      { id: "hover-card", label: "Hover Card" },
                      { id: "input-group", label: "Input Group" },
                      { id: "inputs", label: "Inputs" },
                      { id: "item", label: "Item" },
                      { id: "kbd", label: "Kbd" },
                      { id: "label", label: "Label" },
                      { id: "language-switcher", label: "Language Switcher" },
                      { id: "logo", label: "Logo" },
                      { id: "menubar", label: "Menubar" },
                      { id: "native-select", label: "Native Select" },
                      { id: "navigation-menu", label: "Navigation Menu" },
                      { id: "pagination", label: "Pagination" },
                      { id: "popovers", label: "Popovers" },
                      { id: "progress", label: "Progress" },
                      { id: "radio-group", label: "Radio Group" },
                      { id: "resizable", label: "Resizable" },
                      { id: "scroll-area", label: "Scroll Area" },
                      { id: "selects", label: "Selects" },
                      { id: "separator", label: "Separator" },
                      { id: "sheet", label: "Sheet" },
                      { id: "sidebar", label: "Sidebar" },
                      { id: "skeleton", label: "Skeleton" },
                      { id: "slider", label: "Slider" },
                      { id: "sonner", label: "Sonner" },
                      { id: "spinner", label: "Spinner" },
                      { id: "switches", label: "Switches" },
                      { id: "table", label: "Table" },
                      { id: "tabs", label: "Tabs" },
                      { id: "textarea", label: "Textarea" },
                      { id: "theme-switcher", label: "Theme Switcher" },
                      { id: "toast", label: "Toast" },
                      { id: "toggle", label: "Toggle" },
                      { id: "toggle-group", label: "Toggle Group" },
                      { id: "tooltips", label: "Tooltips" },
                    ],
                  },
                  { id: "how-to-edit", label: "How to Edit" },
                ]}
              />
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="min-w-0 flex-1 space-y-12">
            {/* Introduction */}
            <div
              id="introduction"
              className="scroll-mt-24 rounded-lg border bg-card p-8 text-card-foreground"
            >
              <h2 className="mb-4 text-xl font-semibold">
                Design System Documentation
              </h2>
              <p className="mb-6 text-muted-foreground">
                This page showcases all design tokens and components used in the
                application. Designers can edit the CSS variables in{" "}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  src/index.css
                </code>{" "}
                and see changes reflected in real-time.
              </p>

              {/* Security Notice */}
              <div className="rounded-md border border-yellow-500/20 bg-yellow-500/10 p-4">
                <div className="flex items-start gap-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mt-0.5 flex-shrink-0 text-yellow-600 dark:text-yellow-500"
                  >
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-semibold text-yellow-600 dark:text-yellow-500">
                      Development Only
                    </h3>
                    <p className="mt-1 text-sm text-yellow-600/80 dark:text-yellow-500/80">
                      This route is protected and will never be accessible in
                      production.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator className="my-8" />

            {/* Colors Section */}
            <ColorsSection />

            <Separator className="my-8" />

            {/* Typography Section */}
            <TypographySection />

            <Separator className="my-8" />

            {/* Design Tokens Section */}
            <DesignTokensSection />

            <Separator className="my-8" />

            {/* Icons Section */}
            <IconsSection />

            <Separator className="my-8" />

            {/* Primitives Section */}
            <PrimitivesSection />

            <Separator className="my-8" />

            {/* Composed Section */}
            <ComposedSection />

            <Separator className="my-8" />

            {/* Settings Section */}
            <SettingsSection />

            <Separator className="my-8" />

            {/* How to Edit */}
            <div
              id="how-to-edit"
              className="scroll-mt-24 rounded-lg border bg-card p-8 text-card-foreground"
            >
              <h2 className="mb-4 text-xl font-semibold">How to Edit Colors</h2>
              <div className="space-y-4 text-sm text-muted-foreground">
                <p>
                  All colors are defined as CSS variables in{" "}
                  <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                    src/index.css
                  </code>
                  .
                </p>
                <div className="space-y-2">
                  <p className="font-medium text-foreground">
                    To change a color:
                  </p>
                  <ol className="ml-2 list-inside list-decimal space-y-2">
                    <li>
                      Open{" "}
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                        src/index.css
                      </code>
                    </li>
                    <li>
                      Find the CSS variable under{" "}
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                        :root
                      </code>{" "}
                      (light mode) or{" "}
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                        .dark
                      </code>{" "}
                      (dark mode)
                    </li>
                    <li>
                      Update the HSL values (format:{" "}
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                        H S% L%
                      </code>{" "}
                      without{" "}
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                        hsl()
                      </code>{" "}
                      wrapper)
                    </li>
                    <li>Save and see changes reflected immediately</li>
                  </ol>
                </div>
                <div className="mt-4 rounded-md bg-muted p-4">
                  <p className="mb-2 font-medium text-foreground">Example:</p>
                  <pre className="font-mono text-xs text-foreground">
                    {`:root {
  --primary: 240 5.9% 10%;  /* H=240, S=5.9%, L=10% */
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
